import { fhir, scheduleH1Entry, type HeldRecord } from '@curentia/shared';

/** One supply in the dispensing register: a medicine handed over against a prescription. */
export interface RegisterRow {
  date: string;
  patient: string;
  prescriber: string;
  prescriberRegistration: string;
  /** The prescribing practice's name and address, as the prescription prints them. */
  prescriberAddress: string;
  /** What was handed over. */
  medicine: string;
  /** What the prescription asked for, when a substitute was given. */
  prescribed: string;
  quantity: string;
  batch: string;
  expiry: string;
  /** The Schedule H1 entry the medicine falls under, or an empty text. */
  scheduleH1: string;
  substitution: string;
  pharmacist: string;
  prescriptionId: string;
}

/**
 * The register's rows, oldest first: one for every medicine handed over,
 * read from the practice's dispensing records and the prescriptions they
 * answer. Medicines not dispensed are left out, as nothing was supplied.
 */
export function registerRows(dispenses: readonly HeldRecord[], prescriptions: ReadonlyMap<string, HeldRecord>): RegisterRow[] {
  const rows: RegisterRow[] = [];
  const ordered = [...dispenses].sort((a, b) => (a.date ?? a.createdAt).localeCompare(b.date ?? b.createdAt));
  for (const record of ordered) {
    let dispense: fhir.DispenseView;
    try {
      dispense = fhir.readDispenseRecord(record.resource as unknown as fhir.Bundle);
    } catch {
      continue;
    }
    const held = record.prescriptionId ? prescriptions.get(record.prescriptionId) : undefined;
    let prescription: fhir.PrescriptionView | null = null;
    try {
      prescription = held ? fhir.readPrescription(held.resource as unknown as fhir.Bundle) : null;
    } catch {
      prescription = null;
    }
    const registration = prescription?.doctor.registration;
    const place = [prescription?.practice?.name, prescription?.practice?.address, prescription?.practice?.city].filter(Boolean).join(', ');
    for (const item of dispense.items) {
      if (item.status !== 'completed') continue;
      const prescribed = prescription?.medicines.find((m) => m.requestId === item.requestId)?.name ?? '';
      rows.push({
        date: dispense.date,
        patient: dispense.patient.name,
        prescriber: prescription?.doctor.name ?? '',
        prescriberRegistration: registration ? `${registration.number}, ${registration.council}` : '',
        prescriberAddress: place,
        medicine: item.medicine,
        prescribed: item.substituted ? prescribed : '',
        quantity: item.quantity ? `${item.quantity.value} ${item.quantity.unit}` : '',
        batch: item.batch?.number ?? '',
        expiry: item.batch?.expiry ?? '',
        scheduleH1: scheduleH1Entry(item.medicine) ?? '',
        substitution: item.substituted ? (item.substitutionReason ?? 'Substituted') : '',
        pharmacist: dispense.pharmacist.name,
        prescriptionId: prescription?.id.replace(/^urn:uuid:/, '') ?? '',
      });
    }
  }
  return rows;
}

const COLUMNS: readonly [keyof RegisterRow | 'serial', string][] = [
  ['serial', 'Serial'],
  ['date', 'Date'],
  ['patient', 'Patient'],
  ['prescriber', 'Prescriber'],
  ['prescriberRegistration', 'Prescriber registration'],
  ['prescriberAddress', 'Prescriber address'],
  ['medicine', 'Medicine supplied'],
  ['quantity', 'Quantity'],
  ['batch', 'Batch'],
  ['expiry', 'Expiry'],
  ['scheduleH1', 'Schedule H1'],
  ['prescribed', 'Prescribed'],
  ['substitution', 'Substitution'],
  ['pharmacist', 'Pharmacist'],
  ['prescriptionId', 'Prescription'],
];

/** One CSV cell. Text a spreadsheet would run as a formula starts with an apostrophe instead. */
function cell(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

const day = (iso: string) => (iso ? new Date(iso).toLocaleDateString('en-CA') : '');

/**
 * The register as CSV, numbered from 1 in date order, with a byte order mark
 * so spreadsheet programs read Indian names correctly. It is made on this
 * device and never sent anywhere.
 */
export function registerCsv(rows: readonly RegisterRow[]): string {
  const lines = [COLUMNS.map(([, title]) => cell(title)).join(',')];
  rows.forEach((row, i) => {
    lines.push(COLUMNS.map(([key]) => cell(key === 'serial' ? String(i + 1) : key === 'date' ? day(row.date) : row[key])).join(','));
  });
  return `﻿${lines.join('\r\n')}\r\n`;
}
